import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ToolbarActionConfig } from 'src/blocks/object-toolbar';
import { ObjectToolbar } from 'src/blocks/object-toolbar';
import { useObjectToolbarFormStore } from 'src/blocks/object-toolbar/useObjectToolbarFormStore';
import CustomPdfExportModal from 'src/components/custom-pdf-export-modal/CustomPdfExportModal';

import type { FilterModal } from '@/components/customisable-ribbon/customisableRibbonModalSchema';
import { useExportOptions } from '@/hooks/useExportOptions';
import type { TablePropsWithActions, TableRecord } from '@/utils/table/types';

import { buildAttestationRegisterToolbarConfig } from './registerToolbarConfig';

interface Props<T extends TableRecord> {
  title: string;
  /** Already-formatted count display string (e.g. `getCounter(...)` → `(12)`). */
  counter?: string;
  tableProps: TablePropsWithActions<T>;
  /**
   * Active ribbon filters for the PDF export. Omitted for register views that
   * render no `CustomisableRibbon` (e.g. the By user view).
   */
  getActiveRibbonFilters?: () => FilterModal[];
  /**
   * View-specific inline primary actions. Only the per-user cards view supplies
   * this — its bulk "Attestation Not Required" opener (CUS-176). Other views omit
   * it (export-only registers).
   */
  primaryActions?: ToolbarActionConfig[];
}

/**
 * Object toolbar for the Attestations register views (All / By user / Cycles /
 * per-user cards), mounted only when the `object_toolbar` flag is on. Attestations
 * is an export-only register, so this owns `useExportOptions` here (rather than on
 * the page) to keep it off the legacy `ExportButton` path — with the flag off this
 * component never renders, so the export options are computed once, by whichever
 * path is actually shown (CUS-176, mirroring the Indicators pilot CUS-150).
 *
 * Generic over the view's table-field type so the single component serves every
 * Attestations view without re-declaring the export wiring per view.
 */
export const AttestationRegisterToolbar = <T extends TableRecord>({
  title,
  counter,
  tableProps,
  getActiveRibbonFilters,
  primaryActions,
}: Props<T>) => {
  const { t } = useTranslation(['common']);
  const [showPdfModal, setShowPdfModal] = useState(false);
  // Ribbon "Edit Filters" opener bridged up from CustomisableRibbon (relocate
  // mode). Present only when the user can manage the ribbon; drives the toolbar's
  // overflow "Edit" group.
  const ribbonConfig = useObjectToolbarFormStore((s) => s.ribbonConfigState);
  const onShowCustomPdfModal = useCallback(() => setShowPdfModal(true), []);

  const { exportOptions, exportToPdf } = useExportOptions<T>({
    tableProps,
    entityLabel: title,
    getActiveRibbonFilters,
    onShowCustomPdfModal,
  });

  const toolbarConfig = useMemo(
    () =>
      buildAttestationRegisterToolbarConfig({
        title,
        counter,
        exportOptions,
        primaryActions,
        editRibbon: ribbonConfig
          ? {
              groupLabel: t('edit'),
              label: t('objectToolbar.filterSettings'),
              onClick: ribbonConfig.openEditRibbon,
            }
          : undefined,
      }),
    [title, counter, exportOptions, primaryActions, ribbonConfig, t]
  );

  return (
    <>
      <ObjectToolbar config={toolbarConfig} />
      <CustomPdfExportModal
        exportToPdf={async (options) => {
          await exportToPdf({
            titleOverride: options.title,
            subtitleOverride: options.subtitle,
            hideRibbon: options.hideRibbon,
            orientation: options.orientation,
          });
        }}
        entityLabel={title}
        showModal={showPdfModal}
        setShowModal={setShowPdfModal}
        // Only the ribbon-bearing views (All / Cycles) can hide a ribbon in the
        // PDF. Without `getActiveRibbonFilters`, `useExportOptions` already
        // leaves the ribbon out of the export, so the checkbox would control
        // nothing on the By-user and per-user views.
        showRibbonOptions={Boolean(getActiveRibbonFilters)}
      />
    </>
  );
};
